import React, { useRef } from "react"
import { Popover } from "../shared/components/Popover"
import Card from "../shared/components/Card"
import Typography, {
  typographyTypes,
  TypographyTypes,
} from "../shared/components/Typography"
import Icon from "../shared/components/Icon"
import { MemberIntakePresentingProblemDisplay } from "./memberIntakeDisplay"

type MemberIntakePresentingProblemValueProps = {
  presentingProblem: MemberIntakePresentingProblemDisplay
  textClassName?: string
  typographyVariant?: TypographyTypes
}

const PresentingProblemPart: React.FC<{
  part: MemberIntakePresentingProblemDisplay["primary"]
  textClassName?: string
  typographyVariant?: TypographyTypes
}> = ({ part, textClassName, typographyVariant = typographyTypes.NORMAL }) => {
  const popoverRef = useRef<HTMLDivElement | null>(null)

  if (!part.display) {
    return null
  }

  if (!part.otherText) {
    return (
      <Typography
        text={part.display}
        variant={typographyVariant}
        styleOverrides={textClassName}
      />
    )
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Typography
        text={part.display}
        variant={typographyVariant}
        styleOverrides={textClassName}
      />
      <Popover
        scrollElementRef={popoverRef}
        elementPosition="right"
        triggerElement={
          <Icon iconType="infoQuestionmarkLightGray" styleOverrides="w-4 h-4" />
        }
        styleOverrides="inline-flex items-center p-0"
        containerStyleOverrides="m-0 p-0"
      >
        <Card
          isTooltipCard={true}
          tooltipPosition="right"
          styleOverrides="max-w-sm w-full"
        >
          <Typography
            variant={typographyTypes.NORMAL}
            text={part.otherText}
            styleOverrides="text-gray-900"
          />
        </Card>
      </Popover>
    </span>
  )
}

export const MemberIntakePresentingProblemValue: React.FC<
  MemberIntakePresentingProblemValueProps
> = ({
  presentingProblem,
  textClassName = "text-gray-900",
  typographyVariant = typographyTypes.NORMAL,
}) => {
  const { primary, secondary } = presentingProblem

  if (!primary.display && !secondary.display) {
    return (
      <Typography
        text=""
        variant={typographyVariant}
        styleOverrides={textClassName}
      />
    )
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <PresentingProblemPart
        part={primary}
        textClassName={textClassName}
        typographyVariant={typographyVariant}
      />
      {primary.display && secondary.display && (
        <Typography
          text=" ; "
          variant={typographyVariant}
          styleOverrides={textClassName}
        />
      )}
      <PresentingProblemPart
        part={secondary}
        textClassName={textClassName}
        typographyVariant={typographyVariant}
      />
    </span>
  )
}

export default MemberIntakePresentingProblemValue
